import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PORTFOLIO_CATEGORIES } from "@/data/services";

export default function PortfolioPage() {
  return (
    <div data-testid="portfolio-page" className="min-h-screen">
      {/* Header */}
      <section className="pt-28 pb-12 sm:pt-36 sm:pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            Our Work
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
            Portfolio
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-sm sm:text-base text-white/50 max-w-2xl leading-relaxed">
            Every project tells a story. Browse our work by category to see the craftsmanship and quality we bring to every signage project across Greater Vancouver.
          </motion.p>
        </div>
      </section>

      {/* Category Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {PORTFOLIO_CATEGORIES.map((cat, i) => (
              <motion.div
                key={cat.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/portfolio/${cat.slug}`}
                  className="group relative block rounded-2xl overflow-hidden aspect-[4/3]"
                  data-testid={`portfolio-cat-${cat.slug}`}
                >
                  <img
                    src={`/images/portfolio/${cat.slug}/01.jpg`}
                    alt={cat.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                    <h3 className="font-heading text-base sm:text-lg font-bold text-white group-hover:text-gold transition-colors mb-1">
                      {cat.title}
                    </h3>
                    <p className="text-white/40 text-xs flex items-center gap-1 group-hover:text-white/60 transition-colors">
                      {cat.count} photos <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 section-alt border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Your Business Deserves to <span className="gold-shimmer">Stand Out</span>
          </h2>
          <p className="text-white/50 text-sm sm:text-base mb-10">
            Every project starts with a conversation. Tell us your vision and we'll bring it to life.
          </p>
          <Link to="/booking" data-testid="portfolio-cta-btn" className="inline-flex items-center gap-2 bg-gold text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 text-sm sm:text-base">
            Start Your Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
